/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

import YAML from "yaml";
import * as actions from ".";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Edits, LoadStatus, Topic, TopicRecommender } from "../../../types";
import { readYamlFile } from "../../../utils";

export interface UseWithTopics {
  topics: Record<string, Topic>;
  topicEdits: Record<string, Edits<Topic>>;
  topicLoadStatus: LoadStatus;
  loadAllTopics: () => Promise<Topic[]>;
  loadTopicFromYaml: (data: File | string) => Promise<Topic>;
  createTopic: (newData: Partial<Topic>) => Promise<Topic>;
  deleteTopic: (goal: Topic) => Promise<void>;
  updateTopic: (newData: Partial<Topic>) => Promise<Topic>;
}

export default function useWithTopics(): UseWithTopics {
  const { topics, topicEdits, topicLoadStatus } = useAppSelector(
    (state) => state.topics
  );
  const { lessons: allLessons } = useAppSelector((state) => state.lessons);
  const dispatch = useAppDispatch();

  async function loadAllTopics(): Promise<Topic[]> {
    if (topicLoadStatus === LoadStatus.IN_PROGRESS) return [];
    return await dispatch(actions.loadTopics()).unwrap();
  }

  async function loadTopicFromYaml(file: File | string): Promise<Topic> {
    let data;
    if (typeof file === "string") data = YAML.parse(file);
    else data = await readYamlFile(file);
    const id = typeof data.id === "string" ? data.id : "";
    const name = typeof data.name === "string" ? data.name : "";
    const desc = typeof data.desc === "string" ? data.desc : "";
    const org = typeof data.org === "string" ? data.org : "";
    const hidden = typeof data.hidden === "boolean" ? data.hidden : false;
    const deleted = typeof data.deleted === "boolean" ? data.deleted : false;
    let recommender = TopicRecommender.VALUE;
    if (Object.values(TopicRecommender).includes(data.recommender)) {
      recommender = data.recommender;
    }
    const lessons = [];
    if (Array.isArray(data.lessons)) {
      for (const l of data.lessons) {
        if (typeof l === "string" && allLessons[l]) {
          lessons.push(l);
        }
      }
    }
    const requiredLessons = [];
    if (Array.isArray(data.requiredLessons)) {
      for (const l of data.requiredLessons) {
        if (typeof l === "string" && allLessons[l] && lessons.includes(l)) {
          requiredLessons.push(l);
        }
      }
    }
    const prerequisiteTopics = [];
    if (Array.isArray(data.prerequisiteTopics)) {
      for (const t of data.prerequisiteTopics) {
        if (typeof t === "string" && topics[t]) {
          prerequisiteTopics.push(t);
        }
      }
    }
    const knowledgeComponents = [];
    if (Array.isArray(data.knowledgeComponents)) {
      for (const kc of data.knowledgeComponents) {
        if (typeof kc === "string") {
          knowledgeComponents.push({ kc: kc, relevance: 1 });
        } else if (
          typeof kc === "object" &&
          typeof kc.kc === "string" &&
          typeof kc.relevance === "number"
        ) {
          knowledgeComponents.push({ kc: kc.kc, relevance: kc.relevance });
        }
      }
    }
    const attributeRelevance = [];
    if (Array.isArray(data.attributeRelevance)) {
      for (const a of data.attributeRelevance) {
        if (typeof a === "object" && typeof a.name === "string") {
          attributeRelevance.push({ name: a.name, value: a.value });
        }
      }
    }
    return {
      id,
      name,
      desc,
      hidden,
      deleted,
      org,
      recommender,
      lessons,
      requiredLessons,
      prerequisiteTopics,
      knowledgeComponents,
      attributeRelevance,
    };
  }

  async function createTopic(data: Partial<Topic>): Promise<Topic> {
    if (!data.name) throw new Error("name required");
    if (!data.recommender) throw new Error("recommender required");
    return await dispatch(
      actions.addOrUpdateTopic({
        id: data.id || "",
        name: data.name || "",
        desc: data.desc || "",
        hidden: Boolean(data.hidden),
        deleted: Boolean(data.deleted),
        recommender: data.recommender || TopicRecommender.VALUE,
        lessons: data.lessons || [],
        requiredLessons: data.requiredLessons || [],
        prerequisiteTopics: data.prerequisiteTopics || [],
        knowledgeComponents: data.knowledgeComponents || [],
        attributeRelevance: data.attributeRelevance || [],
      })
    ).unwrap();
  }

  async function deleteTopic(topic: Topic): Promise<void> {
    await dispatch(actions.deleteTopic(topic.id));
  }

  async function updateTopic(data: Partial<Topic>): Promise<Topic> {
    return await dispatch(actions.addOrUpdateTopic(data)).unwrap();
  }

  return {
    topics,
    topicEdits,
    topicLoadStatus,
    loadAllTopics,
    loadTopicFromYaml,
    createTopic,
    deleteTopic,
    updateTopic,
  };
}
